import { Collection, Currency, CurrencyUtil, DateUtil, getApiMetadataFromCache, ModelRow } from ".";
import { DisplayType } from "./constants/DisplayType";
import { DateFormat, ExtendedDateFormat } from "./Date";
import { DateRange } from "./DateRange";
import { NumberUtil } from "./NumberUtil";
import { getRelativeDateString } from "./RelativeDate";

export class DisplayValue {
  public static getDisplayValue(value, displayType: DisplayType, format?: string): string {
    let result = value;
    if (result != null && displayType != null) {
      if (displayType === DisplayType.CURRENCY)
        result = DisplayValue.getCurrencyDisplayValue(result, format);
      else if (displayType === DisplayType.DATETIME || displayType === DisplayType.DATE || displayType === DisplayType.TIME || displayType === DisplayType.DATERANGE)
        result = DisplayValue.getDateDisplayValue(value, displayType, format);
      else if (displayType === DisplayType.DECIMAL || displayType === DisplayType.WEIGHT || displayType === DisplayType.TEMPERATURE) {
        if (typeof result === "string")
          result = parseFloat(NumberUtil.removeFormatting(result));
        result = NumberUtil.formatDecimal(result, format);
      }
      else if (displayType === DisplayType.INTEGER) {
        if (typeof result === "string")
          result = parseInt(NumberUtil.removeFormatting(result));
        result = NumberUtil.formatInteger(result, format);
      }
    }
    return result;
  }

  public static getCurrencyDisplayValue(value: Currency | string | number, format?: string): string {
    if (typeof value === "object")
      return CurrencyUtil.formatCurrency(value as Currency);
    else if (typeof value === "string")
      return CurrencyUtil.formatCurrency({ currency_code: CurrencyUtil.getCurrencySettings().effective_currency_code, amount: parseFloat(CurrencyUtil.parseCurrency(value)) });
    else if (typeof value === "number")
      return CurrencyUtil.formatCurrency({ currency_code: CurrencyUtil.getCurrencySettings().effective_currency_code, amount: value });
  }

  public static getDateDisplayValue(value, displayType: DisplayType, format?: string): string {
    if (format === ExtendedDateFormat.RELATIVE)
      return getRelativeDateString(DateUtil.parseDateTime(value));
    else if (DisplayValue.isExtDateFormat(format)) {
      const extDateFormat = format as ExtendedDateFormat;
      if (displayType === DisplayType.DATE)
        return DateUtil.formatDate(DateUtil.parseDate(value), DateUtil.getDateFormat(DateFormat.DATE, extDateFormat));
      else if (displayType === DisplayType.DATETIME)
        return DateUtil.formatDateTime(DateUtil.parseDateTime(value), DateUtil.getDateFormat(DateFormat.DATE_TIME, extDateFormat));
      else if (displayType === DisplayType.TIME)
        return DateUtil.formatTime(DateUtil.parseTime(value), DateUtil.getDateFormat(DateFormat.TIME, extDateFormat));
      else if (displayType === DisplayType.DATERANGE)
        return DateUtil.formatDateRange(DateRange.parseDateRangeString(value), DateUtil.getDateFormat(DateFormat.DATE, extDateFormat));
    } else {
      if (displayType === DisplayType.DATE)
        return DateUtil.formatDate(DateUtil.parseDate(value), format);
      else if (displayType === DisplayType.DATETIME)
        return DateUtil.formatDateTime(DateUtil.parseDateTime(value), format);
      else if (displayType === DisplayType.TIME)
        return DateUtil.formatTime(DateUtil.parseTime(value), format);
      else if (displayType === DisplayType.DATERANGE)
        return DateUtil.formatDateRange(DateRange.parseDateRangeString(value), format);
    }
  }

  private static isExtDateFormat(format: string): boolean {
    for (const fmt of Object.values(ExtendedDateFormat))
      if (fmt === format)
        return true;
    return false;
  }

  /**
   * Given a format specifier and a ModelRow, this will return a string that formats
   * the ModelRow's data.  Fields are delimited using curly braces.
   *
   * For example, assume you have a row from the customer model with basic address information.
   * If you pass this for the format:
   *
   * Customer: {id} - {name}\n{city}, {state} {zip}
   *
   * The return value will be something like:
   *
   * Customer: MCLBAL - McLeod Software
   * Birmingham, AL 35244
   *
   * @param format
   * @param row
   * @param replacements
   * @returns
   */
  public static getFormattedDataString(format: string, row: ModelRow, replacements?: Collection<string>): string {
    if (format == null)
      return "";
    if (format.indexOf("{") < 0 || format.indexOf("}") < 0)
      return format;
    let result = format;
    const modelRegex = /{[^{}]+}/;
    let match = modelRegex.exec(result);
    while (match != null) {
      let evalValue = "";
      let fieldName = match[0].substring(1, match[0].length - 1);
      const required = fieldName.startsWith("!");
      if (required)
        fieldName = fieldName.substring(1);
      const replacement = replacements == null ? null : replacements[fieldName];
      if (required && replacement == null)
        return "";
      if (replacement != null)
        evalValue = replacement;
      else if (row != null) {
        evalValue = row.get(fieldName);
        if (evalValue == null)
          evalValue = "";
        else {
          const displayType = getApiMetadataFromCache(row._modelPath)?.output[fieldName]?.displayType;
          DisplayValue.getDisplayValue(evalValue, displayType);
        }
      }
      else
        evalValue = "";
      result = result.substring(0, match.index) + evalValue + result.substring(match.index + match[0].length);
      match = modelRegex.exec(result);
    }
    return result;
  }
}
